import { useState, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Text } from '@frontend/components';

import {
  InteractiveFlexAsLink,
  InteractiveFlex,
} from 'components/lib2/InteractiveContainers';

import useGetActiveUserGroupMembers from 'modules/common/userGroupManagement/switchUserGroup/hooks/useGetActiveUserGroupMembers';
import { PopoutContext } from 'modules/notifications/hooks/PopoutContext.js';

import { getGroupPrimaryKey } from 'store/session/selectors';

import { acknowledgeNotification } from '../data/actions';
import type { Notification } from '../types/index';
import { NotificationCircle } from './subcomponents/NotificationCircle';
import NotificationDateAndBusinessName from './subcomponents/NotificationDateAndBusinessName';
import NotificationErrorBoundary from './subcomponents/NotificationErrorBoundary';
import NotificationMessage from './subcomponents/NotificationMessage';
import NotificationsPopover from './subcomponents/NotificationPopover';
import NotificationThumbnail from './subcomponents/NotificationThumbnail';

const NotificationComponent = ({
  notification,
  otherOccurrence,
  otherOccurrences,
}: {
  notification: Notification;
  otherOccurrence?: string;
  otherOccurrences?: string;
}) => {
  const dispatch = useDispatch();
  const { isPopout, closePopout } = useContext(PopoutContext);

  const [isOpen, setIsOpen] = useState(false);

  const groupId = useSelector(getGroupPrimaryKey);
  const { data: userGroupMembers, isLoading } = useGetActiveUserGroupMembers();

  const { id: notificationGroupId } = notification.group;

  const handleClick = () => {
    if (groupId !== notificationGroupId) setIsOpen(true);
    else {
      dispatch(acknowledgeNotification.request({ notification }));
      isPopout && closePopout();
    }
  };

  const otherOccurrencesCount = notification.otherOccurrencesCount;
  const isOther = otherOccurrencesCount > 0 && otherOccurrence;

  return (
    <NotificationErrorBoundary>
      {groupId === notificationGroupId ? (
        <InteractiveFlexAsLink
          onClick={handleClick}
          cursor="pointer"
          to={notification?.relativeUrl || ''}
          paddingTop={2}
          paddingBottom={2}
          paddingRight={2}
          paddingLeft={2}
        >
          <NotificationCircle
            notification={notification}
            isCurrent={groupId === notificationGroupId ? true : false}
          />
          <Box display="flex" flexDirection="column" width="100%">
            <NotificationMessage notification={notification} />
            {isOther && (
              <Text fontSize="11px">
                {`And ${otherOccurrencesCount} other ${
                  otherOccurrencesCount === 1
                    ? otherOccurrence
                    : otherOccurrences
                }`}
              </Text>
            )}
            <NotificationDateAndBusinessName
              notification={notification}
              isCurrent={groupId === notificationGroupId ? true : false}
              isLoading={isLoading}
              userGroupMembers={userGroupMembers}
            />
          </Box>
          {notification.partFileUuid && (
            <NotificationThumbnail notification={notification} />
          )}
        </InteractiveFlexAsLink>
      ) : (
        <>
          <NotificationsPopover
            toggleOpen={setIsOpen}
            isOpen={isOpen}
            nextUrl={notification?.relativeUrl || ''}
            notification={notification}
          >
            <InteractiveFlex
              onClick={handleClick}
              cursor="pointer"
              paddingTop={2}
              paddingBottom={2}
              paddingRight={2}
              paddingLeft={2}
            >
              <NotificationCircle
                notification={notification}
                isCurrent={groupId === notificationGroupId ? true : false}
              />
              <Box display="flex" flexDirection="column" width="100%">
                <NotificationMessage notification={notification} />
                {isOther && (
                  <Text fontSize="11px">
                    {`And ${otherOccurrencesCount} other ${
                      otherOccurrencesCount === 1
                        ? otherOccurrence
                        : otherOccurrences
                    }`}
                  </Text>
                )}
                <NotificationDateAndBusinessName
                  notification={notification}
                  isCurrent={groupId === notificationGroupId ? true : false}
                  isLoading={isLoading}
                  userGroupMembers={userGroupMembers}
                />
              </Box>
              {notification.partFileUuid && (
                <NotificationThumbnail notification={notification} />
              )}
            </InteractiveFlex>
          </NotificationsPopover>
        </>
      )}
    </NotificationErrorBoundary>
  );
};

export default NotificationComponent;
